import { useEffect, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { CheckCircle2, Clock, Truck, Package, XCircle, FileText, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { inr, formatApiError } from "@/lib/api";
import { Reveal } from "@/components/motion";

const STATUS_META = {
  awaiting_payment: { label: "Awaiting Payment", icon: Clock, cls: "bg-amber-50 text-amber-800 border-amber-300" },
  pending_dl_gst: { label: "Pending Details", icon: Clock, cls: "bg-blue-50 text-blue-800 border-blue-300" },
  processing: { label: "Processing", icon: Package, cls: "bg-emerald-50 text-emerald-800 border-emerald-300" },
  shipped: { label: "Shipped", icon: Truck, cls: "bg-indigo-50 text-indigo-800 border-indigo-300" },
  delivered: { label: "Delivered", icon: CheckCircle2, cls: "bg-emerald-50 text-emerald-800 border-emerald-300" },
  cancelled: { label: "Cancelled", icon: XCircle, cls: "bg-red-50 text-red-800 border-red-300" },
};

export function StatusBadge({ status }) {
  const meta = STATUS_META[status] || STATUS_META.processing;
  return (
    <span data-testid="order-status-badge" className={`inline-flex items-center gap-1.5 font-mono-ed text-[10px] uppercase tracking-[0.15em] px-2.5 py-1 border ${meta.cls}`}>
      <meta.icon size={11} /> {meta.label}
    </span>
  );
}

export default function OrderPage() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const [order, setOrder] = useState(null);
  const [error, setError] = useState("");
  const [dl, setDl] = useState("");
  const [gst, setGst] = useState("");
  const [addr, setAddr] = useState({ business_name: "", address: "", city: "", state: "", pincode: "", email: "" });
  const [saving, setSaving] = useState(false);

  const load = () => {
    api.get(`/orders/${id}`, { params: { email: searchParams.get("email") || undefined, phone: searchParams.get("phone") || undefined } })
      .then((r) => setOrder(r.data))
      .catch((e) => setError(formatApiError(e)));
  };
  useEffect(load, [id]);

  const submitB2B = async () => {
    setSaving(true);
    try {
      const { data } = await api.post(`/orders/${id}/b2b-details`, { dl_number: dl, gst_number: gst, ...addr });
      setOrder(data.order);
      toast.success("Delivery & license details recorded");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const detailsValid = addr.business_name.trim() && addr.address.trim() && addr.city.trim() && addr.state.trim() && addr.pincode.trim().length >= 6
    && (!addr.email.trim() || /\S+@\S+\.\S+/.test(addr.email))
    && (!order?.requires_dl_gst || (dl.trim().length >= 5 && gst.trim().length === 15));

  if (error) return (
    <div className="pt-40 pb-32 text-center" data-testid="order-error">
      <p className="font-mono-ed text-xs uppercase tracking-[0.25em] text-slate-400">{error}</p>
      <Link to="/orders" className="text-[#0F5A47] font-mono-ed text-xs uppercase tracking-[0.2em] mt-4 inline-block">Track your order →</Link>
    </div>
  );
  if (!order) return <p className="pt-40 pb-32 text-center font-mono-ed text-xs uppercase tracking-[0.25em] text-slate-400">Loading order…</p>;

  return (
    <div className="max-w-[900px] mx-auto px-5 sm:px-10 pt-28 pb-20" data-testid="order-page">
      <Reveal>
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <p className="font-mono-ed text-[11px] uppercase tracking-[0.3em] text-[#0F5A47] mb-2">Order</p>
            <h1 className="font-display font-extrabold tracking-tight text-4xl" data-testid="order-number">{order.order_number}</h1>
          </div>
          <StatusBadge status={order.status} />
        </div>
      </Reveal>

      {order.status === "pending_dl_gst" && (
        <div className="border border-blue-200 bg-[#EFF6FF]/60 p-6 mt-8" data-testid="order-b2b-pending">
          <h3 className="font-display font-bold">Action required — delivery {order.requires_dl_gst ? "& license" : ""} details</h3>
          <p className="text-xs text-slate-500 mt-1">
            Tell us where to deliver this order.{order.requires_dl_gst && " Also provide your Drug License and GST number to release this B2B Rx order for dispatch."}
          </p>
          <p className="font-mono-ed text-[10px] uppercase tracking-[0.2em] text-slate-500 mt-4 mb-2">Business & Delivery Details</p>
          <div className="grid sm:grid-cols-2 gap-3" data-testid="delivery-address-form">
            <input data-testid="b2b-business-name-input" className="border hairline bg-white px-4 py-3 text-sm focus:outline-none focus:border-[#1D4ED8] sm:col-span-2" placeholder="Business / shop / clinic name" value={addr.business_name} onChange={(e) => setAddr({ ...addr, business_name: e.target.value })} />
            <input data-testid="b2b-address-input" className="border hairline bg-white px-4 py-3 text-sm focus:outline-none focus:border-[#1D4ED8] sm:col-span-2" placeholder="Shop / clinic street address" value={addr.address} onChange={(e) => setAddr({ ...addr, address: e.target.value })} />
            <input data-testid="b2b-city-input" className="border hairline bg-white px-4 py-3 text-sm focus:outline-none focus:border-[#1D4ED8]" placeholder="City" value={addr.city} onChange={(e) => setAddr({ ...addr, city: e.target.value })} />
            <input data-testid="b2b-state-input" className="border hairline bg-white px-4 py-3 text-sm focus:outline-none focus:border-[#1D4ED8]" placeholder="State" value={addr.state} onChange={(e) => setAddr({ ...addr, state: e.target.value })} />
            <input data-testid="b2b-pincode-input" className="border hairline bg-white px-4 py-3 text-sm focus:outline-none focus:border-[#1D4ED8]" placeholder="PIN code (6 digits)" maxLength={6} value={addr.pincode} onChange={(e) => setAddr({ ...addr, pincode: e.target.value })} />
            <input data-testid="b2b-email-input" className="border hairline bg-white px-4 py-3 text-sm focus:outline-none focus:border-[#1D4ED8]" placeholder="Email (optional — for order updates)" type="email" value={addr.email} onChange={(e) => setAddr({ ...addr, email: e.target.value })} />
          </div>
          {order.requires_dl_gst && (
            <div className="grid sm:grid-cols-2 gap-3 mt-3">
              <input data-testid="b2b-dl-number-input" className="border hairline bg-white px-4 py-3 text-sm focus:outline-none focus:border-[#1D4ED8]" placeholder="DL Number" value={dl} onChange={(e) => setDl(e.target.value)} />
              <input data-testid="b2b-gst-number-input" className="border hairline bg-white px-4 py-3 text-sm focus:outline-none focus:border-[#1D4ED8]" placeholder="GST Number (15 chars)" maxLength={15} value={gst} onChange={(e) => setGst(e.target.value)} />
            </div>
          )}
          <button data-testid="b2b-submit-btn" onClick={submitB2B} disabled={saving || !detailsValid} className="mt-4 bg-[#1D4ED8] text-white px-8 py-3 font-mono-ed text-xs uppercase tracking-[0.2em] hover:bg-blue-800 transition-colors disabled:opacity-40 flex items-center gap-2">
            {saving && <Loader2 size={13} className="animate-spin" />} Submit
          </button>
        </div>
      )}

      <Reveal delay={0.1}>
        <div className="border hairline bg-white mt-8 divide-y divide-[#E2E8F0]">
          {order.items.map((i, idx) => (
            <div key={idx} className="flex justify-between px-6 py-4 text-sm">
              <div>
                <p className="font-medium">{i.name} {i.rx && <span className="font-mono-ed text-[8px] text-red-700 border border-red-300 px-1 ml-1">Rx</span>}</p>
                <p className="font-mono-ed text-[10px] text-slate-400">{(() => { const bx = i.boxes ?? i.qty * (parseInt(i.pack, 10) || 1); return `${bx} boxes (${(bx * 10).toLocaleString("en-IN")} strips)`; })()}</p>
              </div>
              <span className="font-mono-ed">{inr(i.line_total)}</span>
            </div>
          ))}
          {order.subtotal != null && (
            <div className="flex justify-between px-6 py-3 font-mono-ed text-sm">
              <span className="uppercase text-[11px] tracking-[0.2em] text-slate-500">Subtotal (Net Rate)</span>
              <span data-testid="order-subtotal">{inr(order.subtotal)}</span>
            </div>
          )}
          {order.discount_amount > 0 && (
            <div className="flex justify-between px-6 py-3 font-mono-ed text-sm text-emerald-700">
              <span className="uppercase text-[11px] tracking-[0.2em]">Referral Discount ({order.referral_code})</span>
              <span data-testid="order-discount">−{inr(order.discount_amount)}</span>
            </div>
          )}
          {order.gst_amount != null && (
            <div className="flex justify-between px-6 py-3 font-mono-ed text-sm">
              <span className="uppercase text-[11px] tracking-[0.2em] text-slate-500">GST (5%)</span>
              <span data-testid="order-gst">{inr(order.gst_amount)}</span>
            </div>
          )}
          {order.scheme?.free_boxes > 0 && (
            <div className="flex justify-between px-6 py-3 font-mono-ed text-sm text-emerald-700">
              <span className="uppercase text-[11px] tracking-[0.2em]">Free Boxes (Scheme)</span>
              <span className="font-semibold" data-testid="order-free-boxes">+{order.scheme.free_boxes} boxes · ₹0.00 FREE</span>
            </div>
          )}
          <div className="flex justify-between px-6 py-4 font-mono-ed font-semibold">
            <span className="uppercase text-[11px] tracking-[0.2em] text-slate-500 font-normal">Total paid</span>
            <span data-testid="order-total">{inr(order.total)}</span>
          </div>
        </div>
      </Reveal>

      <Reveal delay={0.15}>
        <div className="grid sm:grid-cols-2 gap-6 mt-8">
          <div className="border hairline bg-white p-6">
            <p className="font-mono-ed text-[10px] uppercase tracking-[0.25em] text-slate-400 mb-3">Delivery To</p>
            <p className="text-sm font-medium">{order.customer.business_name || order.customer.name} <span className="font-mono-ed text-[9px] uppercase text-[#1D4ED8] ml-1">{order.buyer_type}</span></p>
            {order.customer.business_name && <p className="text-xs text-slate-400 mt-0.5">Contact: {order.customer.name}</p>}
            {order.customer.address
              ? <p className="text-sm text-slate-500 mt-1">{order.customer.address}, {order.customer.city}, {order.customer.state} — {order.customer.pincode}</p>
              : <p className="text-sm text-amber-700 mt-1">Delivery address pending — submit it in the form above.</p>}
            <p className="text-sm text-slate-500 mt-1">{order.customer.phone}{order.customer.email ? ` · ${order.customer.email}` : ""}</p>
          </div>
          <div className="border hairline bg-white p-6">
            <p className="font-mono-ed text-[10px] uppercase tracking-[0.25em] text-slate-400 mb-3">Compliance</p>
            <div className="space-y-2 text-sm">
              {order.prescription_path && (
                <a data-testid="order-prescription-link" href={`${process.env.REACT_APP_BACKEND_URL}/api/files/${order.prescription_path}`} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-[#0F5A47] hover:underline">
                  <FileText size={14} /> View uploaded prescription
                </a>
              )}
              {order.dl_number && <p className="font-mono-ed text-xs">DL: {order.dl_number}</p>}
              {order.gst_number && <p className="font-mono-ed text-xs">GST: {order.gst_number}</p>}
              <p className="font-mono-ed text-xs text-slate-400">Payment: {order.payment.provider}{order.payment.mock ? " (demo)" : ""} · {order.payment.status}</p>
            </div>
          </div>
        </div>
      </Reveal>
    </div>
  );
}
